import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Loader2, Search } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

type ReferralCode = Tables<'referral_codes'>;

export default function ReferralCodesTab() {
  const qc = useQueryClient();
  const { data: codes = [], isLoading } = useQuery({
    queryKey: ['referral_codes'],
    queryFn: async () => {
      const { data, error } = await supabase.from('referral_codes').select('*, profiles:owner_user_id(full_name, email)').order('created_at', { ascending: false });
      if (error) throw error;
      return data as (ReferralCode & { profiles: { full_name: string; email: string } | null })[];
    },
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ['profiles_for_referrals'],
    queryFn: async () => {
      const { data, error } = await supabase.from('profiles').select('id, full_name, email').order('full_name');
      if (error) throw error;
      return data;
    },
  });

  const createMut = useMutation({
    mutationFn: async (input: any) => { const { error } = await supabase.from('referral_codes').insert(input); if (error) throw error; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['referral_codes'] }),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, ...input }: any) => { const { error } = await supabase.from('referral_codes').update(input).eq('id', id); if (error) throw error; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['referral_codes'] }),
  });
  const deleteMut = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('referral_codes').delete().eq('id', id); if (error) throw error; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['referral_codes'] }),
  });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [dialog, setDialog] = useState<{ open: boolean; item?: ReferralCode }>({ open: false });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({ code: '', owner_user_id: '', commission_rate: '', active: true });

  const filtered = codes.filter(c => {
    const q = search.toLowerCase();
    if (q && !c.code.toLowerCase().includes(q) && !(c.profiles?.full_name || '').toLowerCase().includes(q) && !(c.profiles?.email || '').toLowerCase().includes(q)) return false;
    if (statusFilter === 'active' && !c.active) return false;
    if (statusFilter === 'inactive' && c.active) return false;
    return true;
  });

  const openDialog = (item?: ReferralCode) => {
    setForm(item ? { code: item.code, owner_user_id: item.owner_user_id, commission_rate: String(item.commission_rate ?? ''), active: item.active ?? true } : { code: '', owner_user_id: '', commission_rate: '', active: true });
    setDialog({ open: true, item });
  };

  const save = async () => {
    if (!form.code || !form.owner_user_id) return;
    const payload = { code: form.code, owner_user_id: form.owner_user_id, commission_rate: form.commission_rate ? Number(form.commission_rate) : null, active: form.active };
    try {
      if (dialog.item) { await updateMut.mutateAsync({ id: dialog.item.id, ...payload }); }
      else { await createMut.mutateAsync(payload); }
      setDialog({ open: false });
      toast({ title: dialog.item ? 'Código actualizado' : 'Código agregado' });
    } catch { toast({ title: 'Error al guardar', variant: 'destructive' }); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); setDeleteId(null); toast({ title: 'Código eliminado' }); }
    catch { toast({ title: 'Error al eliminar', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar por código o propietario…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            <SelectItem value="active">Activo</SelectItem>
            <SelectItem value="inactive">Inactivo</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => openDialog()} size="sm"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
      </div>
      <p className="text-xs text-muted-foreground">{filtered.length} de {codes.length} códigos</p>
      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Propietario</TableHead>
                <TableHead>Comisión %</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-24">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium font-mono">{c.code}</TableCell>
                  <TableCell>{c.profiles?.full_name || c.profiles?.email || '—'}</TableCell>
                  <TableCell>{c.commission_rate != null ? `${c.commission_rate}%` : '—'}</TableCell>
                  <TableCell><Badge variant={c.active ? 'default' : 'secondary'}>{c.active ? 'Activo' : 'Inactivo'}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openDialog(c)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(c.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(open) => !open && setDialog({ open: false })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog.item ? 'Editar Código' : 'Agregar Código'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Código</Label><Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="Ej: REF2024" /></div>
            <div>
              <Label>Propietario</Label>
              <select className="w-full border rounded-md p-2 text-sm bg-background" value={form.owner_user_id} onChange={e => setForm(f => ({ ...f, owner_user_id: e.target.value }))}>
                <option value="">Seleccionar usuario</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.full_name || p.email}</option>)}
              </select>
            </div>
            <div><Label>Comisión (%)</Label><Input type="number" value={form.commission_rate} onChange={e => setForm(f => ({ ...f, commission_rate: e.target.value }))} placeholder="Ej: 10" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
              <Label>Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false })}>Cancelar</Button>
            <Button onClick={save} disabled={!form.code || !form.owner_user_id}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este código?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
